/**
 * Agent Profile Page — /the-team/{slug}
 * ---------------------------------------------------------------------------
 * Split-hero + full-width editorial flow:
 *   1. Hero — cinematic portrait (65%) + identity panel (35%)
 *   2. How It Works (teal)
 *   3. Cinema Band — "For the Apex Traveler" (video)
 *   4. Why Luxrally (ivory)
 *   5. Reviews (teal, if agent has reviews)
 *   6. Exclusive Access — partner benefits (teal)
 *   7. Closing CTA — "Ready to start planning?" (teal)
 *   8. Articles / Journal (ivory, if agent has articles)
 *
 * Single-file component (no sub-file splitting).
 */

import { TaglineBadge } from '@/components/brand/TaglineBadge';
import { InquiryForm } from '@/components/inquiry/InquiryForm';
import { useInquiryModal, type InquiryContext } from '@/components/inquiry/InquiryModal';
import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { logoPictureSources, partnerLogoIntrinsicSize } from '@/lib/responsive-image';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import languagesData from '../../../data/languages.json';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle, ChevronDown, UserPlus, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AgentExpertise {
    id: number;
    slug: string;
    label: string;
}

interface AgentContent {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    role: string | null;
    bio: string | null;
    phone: string | null;
    email: string | null;
    main_image: string | null;
    is_luxury: boolean;
    show_save_contact?: boolean;
    became_agent_on?: string | null;
    show_became_agent_on_publicly?: boolean;
    slug: string;
    author_slug?: string | null;
    // Profile fields populated by PageResolverService::buildAgentProfilePayload
    based_in?: string | null;
    languages?: string[];
    instagram_handle?: string | null;
    twitter_handle?: string | null;
    facebook_handle?: string | null;
    linkedin_url?: string | null;
    tiktok_handle?: string | null;
    pinterest_handle?: string | null;
    youtube_url?: string | null;
    reddit_handle?: string | null;
    website_url?: string | null;
    /** CRM-issued client sign-up link (r77) — renders the "Create your account" CTA; absent = no CTA. */
    client_referral_url?: string | null;
    expertises?: AgentExpertise[];
}

interface AgentGuide {
    id: number;
    title: string;
    excerpt: string | null;
    main_image: string | null;
    slug: string;
    published_at: string | null;
    tags: string | null;
    author_name: string | null;
}

interface AgentReview {
    id: number;
    client_name: string;
    rating: number;
    review_text: string;
    created_at: string;
}

interface AgentShowProps {
    page: unknown;
    content: unknown;
    metadata: unknown;
    dataContext: {
        agent?: AgentContent;
        author_guides?: AgentGuide[];
        default_expertises?: string[];
        [key: string]: unknown;
    } | null;
    agentReviews: AgentReview[];
    // Set by PageController::preview in the CMS. FAKE_AGENT fallback data is
    // only rendered when this is true — public paths are guarded by the backend
    // (PageController::show 404s when the agentsBySlug data source returns
    // empty), so in production dataContext.agent will always be populated.
    isPreview?: boolean;
}

interface ReviewFormData {
    name: string;
    email: string;
    rating: number;
    review_text: string;
    website: string; // honeypot
}

interface AgentSocials {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
    twitter?: string;
    tiktok?: string;
    pinterest?: string;
    youtube?: string;
    reddit?: string;
    website?: string;
}

interface AgentProfile {
    languages: string[];
    specialties: string[];
    based_in: string | null;
    socials?: AgentSocials;
}

/* ------------------------------------------------------------------ */
/*  Animation Tokens                                                   */
/* ------------------------------------------------------------------ */

/* Form input — light-on-dark (drawer, teal sections).
 * Typography mirrors InquiryForm's `inquiry-field` for legibility:
 * Inter normal-weight 14px, no letter-spacing pull. Opacity tokens
 * follow the locked dark-form contrast spec (border /30, placeholder
 * /55, focus /55). */
const INPUT_CLS =
    'w-full border-b border-[#F5F5F5]/30 bg-transparent px-0 py-3 text-[14px] font-normal text-[#F5F5F5] transition-colors duration-300 placeholder:text-[#F5F5F5]/55 focus:border-[#F5F5F5]/55 focus:outline-none';

const INPUT_CLS_LIGHT =
    'lux-body lux-body-sm w-full border-b border-[#0e2727]/15 bg-transparent px-0 py-2.5 text-[#0e2727] transition-colors duration-300 placeholder:text-[#0e2727]/35 focus:border-[#0e2727]/40 focus:outline-none';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.15 } },
};

/* Button — dark frosted glass (dark backgrounds + header CTA) */
const CTA_BTN =
    'lux-caps lux-caps-sm rounded-sm border border-[#F5F5F5]/30 bg-black/15 text-[#F5F5F5] backdrop-blur-sm transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-black/25';

/* Button — primary solid teal (r77 "Create your account" — the CRM client sign-up link) */
const CTA_BTN_PRIMARY =
    'lux-caps lux-caps-sm inline-flex items-center justify-center rounded-sm border border-[#F5F5F5]/20 bg-[#0e2727] text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/40 hover:bg-[#143333] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]';

/* Button — dark-on-light (ivory backgrounds) */
const CTA_BTN_DARK =
    'lux-caps lux-caps-sm rounded-sm border border-[#0e2727]/30 bg-[#0e2727]/10 text-[#0e2727] transition-colors duration-300 hover:border-[#0e2727]/50 hover:bg-[#0e2727]/15';

/* ------------------------------------------------------------------ */
/*  vCard Helper                                                        */
/* ------------------------------------------------------------------ */

function buildVCard(agent: AgentContent): string {
    const lines = [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${agent.full_name}`,
        `N:${agent.last_name};${agent.first_name};;;`,
        agent.role ? `TITLE:${agent.role}` : '',
        'ORG:LuxRally Travel',
        agent.phone ? `TEL;TYPE=CELL:${agent.phone}` : '',
        agent.email ? `EMAIL:${agent.email}` : '',
        `URL:https://luxrallytravel.com/the-team/${agent.slug}`,
        'END:VCARD',
    ];
    return lines.filter(Boolean).join('\n');
}

function buildVCardDataUri(agent: AgentContent): string {
    return `data:text/vcard;charset=utf-8,${encodeURIComponent(buildVCard(agent))}`;
}

/* ------------------------------------------------------------------ */
/*  Static Data                                                        */
/* ------------------------------------------------------------------ */

const BUDGET_TIERS = [
    'Under $5,000',
    '$5,000 – $10,000',
    '$10,000 – $25,000',
    '$25,000 – $50,000',
    '$50,000 – $100,000',
    '$100,000 – $250,000',
    '$250,000+',
];

/* ------------------------------------------------------------------ */
/*  Fake Data (development / preview)                                  */
/* ------------------------------------------------------------------ */

const FAKE_AGENT: AgentContent = {
    id: 999,
    first_name: 'Alexandra',
    last_name: 'Montague',
    full_name: 'Alexandra Montague',
    role: 'Travel Advisor',
    bio: "<p>With over twelve years of experience curating bespoke journeys across Europe and East Africa, Alexandra has built a reputation for transforming travel dreams into extraordinary realities. Her deep relationships with the world's finest properties — from clifftop villas on the Amalfi Coast to private conservancies in the Maasai Mara — give her clients access that no booking engine can match.</p>",
    phone: '+15557248831',
    email: 'alexandra@luxrallytravel.com',
    main_image: null,
    is_luxury: true,
    slug: '__fake_agent_preview__',
};

/* ISO-639-1 → display label map, derived from the canonical resources/data/languages.json
   that the agent dashboard uses for selection. Single source of truth — adding a code
   there makes it render correctly here automatically. */
const LANGUAGE_LABELS: Record<string, string> = Object.fromEntries(
    (languagesData as Array<{ code: string; name: string }>).map(({ code, name }) => [code, name]),
);

/* FAKE_PROFILE — used only when the page renders without a real agent record
   (design-preview / storybook-style dev path paired with FAKE_AGENT). */
const FAKE_PROFILE: AgentProfile = {
    languages: ['English', 'French', 'Italian'],
    specialties: ['Honeymoon', 'Safari', 'Family', 'Culinary', 'Wellness'],
    based_in: 'New York, NY',
    socials: {
        instagram: 'https://instagram.com/luxrallytravel',
        facebook: 'https://facebook.com/luxrallytravel',
        linkedin: 'https://linkedin.com/in/luxrallytravel',
    },
};

const FAKE_REVIEWS: AgentReview[] = [
    {
        id: 1,
        client_name: 'Catherine & James W.',
        rating: 5,
        review_text:
            'Alexandra planned our 10th anniversary trip to the Maldives and it was nothing short of magical. Every detail was perfect — from the seaplane transfer to the private sandbank dinner under the stars. We felt like royalty the entire trip.',
        created_at: '2025-11-14T00:00:00Z',
    },
    {
        id: 2,
        client_name: 'Michael R.',
        rating: 5,
        review_text:
            'Our family safari in Kenya exceeded every expectation. Alexandra secured us a private conservancy that most people never hear about. The kids are still talking about the baby elephants six months later. Truly life-changing.',
        created_at: '2025-09-02T00:00:00Z',
    },
    {
        id: 3,
        client_name: 'Sophia L.',
        rating: 5,
        review_text:
            'I came to Alexandra with a vague idea of "somewhere beautiful in Italy" and she delivered an itinerary that felt like it was pulled from a film. Private cooking classes, vineyard lunches, a sunset boat ride on Lake Como. Perfection.',
        created_at: '2025-07-20T00:00:00Z',
    },
    {
        id: 4,
        client_name: 'David & Priya K.',
        rating: 4,
        review_text:
            'Exceptional service from start to finish. Alexandra navigated some tricky scheduling for our Japan cherry blossom trip and made sure we had VIP access at every ryokan. The only reason for four stars is I wish it could have been longer!',
        created_at: '2025-04-18T00:00:00Z',
    },
    {
        id: 5,
        client_name: 'Eleanor M.',
        rating: 5,
        review_text:
            'As a solo traveler, I was nervous about planning a luxury trip on my own. Alexandra made me feel completely taken care of. My Bora Bora wellness retreat was the best gift I have ever given myself. Already planning my next trip with her.',
        created_at: '2025-02-08T00:00:00Z',
    },
    {
        id: 6,
        client_name: 'Richard & Anne T.',
        rating: 5,
        review_text:
            'We have traveled extensively, but Alexandra introduced us to a side of Morocco we never knew existed. A private riad in the medina, a Berber guide through the Atlas Mountains, and a dinner under the stars in the Sahara. Unforgettable.',
        created_at: '2024-12-19T00:00:00Z',
    },
    {
        id: 7,
        client_name: 'Natasha V.',
        rating: 5,
        review_text:
            'Alexandra organized a surprise honeymoon for us in Santorini and Crete. Every hotel had a handwritten welcome note and our favorite wine chilling in the room. She remembered details I had mentioned months earlier. That level of care is rare.',
        created_at: '2024-10-05T00:00:00Z',
    },
    {
        id: 8,
        client_name: 'Thomas & Julia H.',
        rating: 4,
        review_text:
            'Our three-week Southeast Asia trip — Thailand, Vietnam, Cambodia — was flawlessly coordinated. Alexandra handled every transfer, every visa detail, and got us upgraded at two properties. The only hiccup was a delayed domestic flight, but she had us rebooked before we even noticed.',
        created_at: '2024-08-22T00:00:00Z',
    },
    {
        id: 9,
        client_name: 'Margaret S.',
        rating: 5,
        review_text:
            'I asked Alexandra to plan a birthday trip for my 70th — something I would never forget. She delivered: a week in the Scottish Highlands with a private castle stay, whisky tastings, and a helicopter ride over the Isle of Skye. My family is still speechless.',
        created_at: '2024-06-14T00:00:00Z',
    },
    {
        id: 10,
        client_name: 'James & Olivia P.',
        rating: 5,
        review_text:
            'We have used Alexandra for four trips now — Patagonia, New Zealand, Iceland, and most recently the Galápagos. Each one has been better than the last. She understands what we want before we do. We will never book a trip without her again.',
        created_at: '2024-04-30T00:00:00Z',
    },
    {
        id: 11,
        client_name: 'Daniel C.',
        rating: 5,
        review_text:
            'Alexandra planned a solo diving trip to the Maldives and Raja Ampat. She found a liveaboard I never would have discovered on my own and negotiated a rate that saved me over $2,000. The service was extraordinary from start to finish.',
        created_at: '2024-03-11T00:00:00Z',
    },
    {
        id: 12,
        client_name: 'Sarah & Mark B.',
        rating: 5,
        review_text:
            'Our family of six spent two weeks in South Africa — Cape Town, the Winelands, and a private game reserve. Alexandra coordinated every detail for the kids and the adults. The children had their own safari guide and the adults had time to relax. Perfection.',
        created_at: '2024-01-25T00:00:00Z',
    },
];

const FAKE_ARTICLES: AgentGuide[] = [
    {
        id: 101,
        title: "Why Kenya's Private Conservancies Are the Future of Safari",
        excerpt:
            'Forget the crowded national parks. Alexandra reveals the exclusive conservancies that offer intimate wildlife encounters and direct community impact.',
        main_image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&h=400&fit=crop',
        slug: 'kenya-private-conservancies',
        published_at: '2025-10-15T00:00:00Z',
        tags: 'Safari, Kenya, Conservation',
        author_name: 'Alexandra Montague',
    },
    {
        id: 102,
        title: "The Amalfi Coast in September: An Insider's Guide",
        excerpt:
            "September is the sweet spot — golden light, warm seas, and far fewer crowds. Here's how to experience the Amalfi Coast like a local.",
        main_image: 'https://images.unsplash.com/photo-1534113414509-0eec2bfb493f?w=600&h=400&fit=crop',
        slug: 'amalfi-coast-september-guide',
        published_at: '2025-08-28T00:00:00Z',
        tags: 'Italy, Europe, Culinary',
        author_name: 'Alexandra Montague',
    },
    {
        id: 103,
        title: "A First-Timer's Guide to Japanese Ryokans",
        excerpt: 'From onsen etiquette to kaiseki dining, everything you need to know before booking your first traditional Japanese inn.',
        main_image: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=600&h=400&fit=crop',
        slug: 'japanese-ryokans-guide',
        published_at: '2025-06-10T00:00:00Z',
        tags: 'Japan, Asia, Wellness',
        author_name: 'Alexandra Montague',
    },
    {
        id: 104,
        title: 'The Case for Slow Travel in Provence',
        excerpt: 'Why rushing through the lavender fields defeats the purpose — and how to plan a week that lets Provence unfold at its own pace.',
        main_image: 'https://images.unsplash.com/photo-1499002238440-d264edd596ec?w=600&h=400&fit=crop',
        slug: 'slow-travel-provence',
        published_at: '2025-04-22T00:00:00Z',
        tags: 'France, Europe, Wellness',
        author_name: 'Alexandra Montague',
    },
    {
        id: 105,
        title: 'Maldives Beyond the Overwater Villa',
        excerpt:
            'Everyone books the overwater bungalow. Here are the experiences — from submarine dives to sandbank picnics — that actually make a Maldives trip unforgettable.',
        main_image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=600&h=400&fit=crop',
        slug: 'maldives-beyond-overwater-villa',
        published_at: '2025-03-05T00:00:00Z',
        tags: 'Maldives, Honeymoon, Adventure',
        author_name: 'Alexandra Montague',
    },
    {
        id: 106,
        title: 'Planning a Multi-Generational Safari: What Nobody Tells You',
        excerpt:
            'Traveling with grandparents and grandchildren requires a different kind of safari. The right conservancy, pace, and guide make all the difference.',
        main_image: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600&h=400&fit=crop',
        slug: 'multi-generational-safari',
        published_at: '2025-01-18T00:00:00Z',
        tags: 'Safari, Family, Africa',
        author_name: 'Alexandra Montague',
    },
    {
        id: 107,
        title: 'Winter in the Swiss Alps: Beyond the Ski Lodge',
        excerpt: 'Fondue, thermal baths, scenic trains, and star-lit walks — a guide to the Alps for travelers who want luxury without the moguls.',
        main_image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=600&h=400&fit=crop',
        slug: 'swiss-alps-winter-guide',
        published_at: '2024-11-30T00:00:00Z',
        tags: 'Switzerland, Europe, Wellness',
        author_name: 'Alexandra Montague',
    },
    {
        id: 108,
        title: 'How to Actually Unplug: A Digital Detox Itinerary',
        excerpt: 'No Wi-Fi, no screens, no guilt. Three destinations where disconnecting is the entire point — and your phone can wait.',
        main_image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&h=400&fit=crop',
        slug: 'digital-detox-itinerary',
        published_at: '2024-09-14T00:00:00Z',
        tags: 'Wellness, Adventure, Off-Grid',
        author_name: 'Alexandra Montague',
    },
    {
        id: 109,
        title: 'Patagonia in Three Acts: Glaciers, Gauchos, and Gastronomy',
        excerpt:
            'From the Perito Moreno to a private estancia, a curated route through Argentine and Chilean Patagonia that balances adventure with comfort.',
        main_image: 'https://images.unsplash.com/photo-1478827536114-da961b7f86d2?w=600&h=400&fit=crop',
        slug: 'patagonia-three-acts',
        published_at: '2024-07-08T00:00:00Z',
        tags: 'Patagonia, South America, Adventure',
        author_name: 'Alexandra Montague',
    },
];

/* ------------------------------------------------------------------ */
/*  How It Works — 4 Steps                                             */
/* ------------------------------------------------------------------ */

function buildHowItWorksSteps(firstName: string) {
    return [
        {
            num: '1',
            title: 'Sign Up',
            body: `Complete a brief registration and share your plans. ${firstName} will reach out personally to align on vision and priorities.`,
        },
        {
            num: '2',
            title: 'Connect',
            body: `${firstName} will talk through options and send a quote. Our tech gives us pricing visibility into non-public rates meaning you won't overpay.`,
        },
        {
            num: '3',
            title: 'Confirm',
            body: 'Confirm your selection and submit a secure authorization form. Your profile is stored, allowing future bookings to be handled with a simple text.',
        },
        {
            num: '4',
            title: 'Relax',
            body: `Before, during, and after your trip, ${firstName} remains directly available by call or text, managing changes and handling whatever arises along the way.`,
        },
    ];
}

/* ------------------------------------------------------------------ */
/*  Why Luxrally — 6 Value Props                                       */
/* ------------------------------------------------------------------ */

const VALUE_PROPS = [
    {
        kicker: 'Better Rates',
        headline: 'The Rates You Never See',
        body: 'Hotels distribute thousands of unpublished rates through private channels — wholesalers, consortia, agency networks. We scan hundreds of these sources in seconds so you\u2019re never paying more than you should.',
    },
    {
        kicker: 'Recognition',
        headline: 'They Know You\u2019re Coming',
        body: 'When you book through us, your preferences arrive before you do. The room, the pillow, the wine you like — all communicated in advance. You walk in as a known guest, not a stranger checking in.',
    },
    {
        kicker: 'Advocacy',
        headline: 'Someone on Your Side',
        body: 'You reach your advisor directly. They speak to hotel management — not the front desk. No hold music. No repeating yourself. Actual representation when something goes wrong.',
    },
    {
        kicker: 'Zero Fees',
        headline: 'So What\u2019s the Catch',
        body: 'There isn\u2019t one. The hotel pays us — not you. You pay the same rate, often less, and get better treatment. It doesn\u2019t cost you a thing.',
    },
    {
        kicker: 'Convenience',
        headline: 'It Only Gets Better',
        body: 'Each booking becomes faster than the last. Call or text your advisor with a destination — they\u2019ll come back with options before you\u2019ve finished thinking about it.',
    },
    {
        kicker: 'Personalization',
        headline: 'A Permanent Advantage',
        body: 'Your preferences, loyalty numbers, and travel history are stored securely. Future bookings take a single text. A relationship that compounds every time you travel.',
    },
];

/* ------------------------------------------------------------------ */
/*  Benefit Partner Logos (for marquee scroller)                        */
/* ------------------------------------------------------------------ */

const BENEFIT_LOGOS = [
    {
        name: 'Four Seasons Preferred Partners',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/four-seasons-preferred-partners-trimmed.png',
        url: '/four-seasons-preferred-partners',
    },
    { name: 'Hyatt Privé', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/hyatt-prive-trimmed.png', url: '/hyatt-prive' },
    {
        name: 'Mandarin Oriental Fan Club',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/mandarin-oriental-fan-club-trimmed.png',
        url: '/mandarin-oriental-fan-club',
    },
    { name: 'Rosewood Elite', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/rosewood-elite-trimmed.png', url: '/rosewood-elite-benefits' },
    { name: 'Peninsula PenClub', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/peninsula-penclub-trimmed.png', url: '/peninsula-penclub' },
    {
        name: 'Belmond Bellini Club',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/belmond-bellini-club-trimmed.png',
        url: '/belmond-bellini-club-',
    },
    {
        name: 'Shangri-La Luxury Circle',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/shangri-la-luxury-circle-trimmed.png',
        url: '/shangri-la-luxury-circle',
    },
    { name: 'Hilton For Luxury', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/hilton-for-luxury-trimmed.png', url: '/hilton-for-luxury' },
    {
        name: 'Jumeirah Passport to Luxury',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/jumeirah-passport-to-luxury-trimmed.png',
        url: '/jumeirah-passport-to-luxury',
    },
    {
        name: 'Couture by Langham',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/couture-by-langham-trimmed.png',
        url: '/couture-by-langham',
    },
    { name: 'IHG Destined', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/ihg-destined-trimmed.png', url: '/ihg-destined' },
    { name: 'Accor Preferred HERA', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/accor-preferred-hera-trimmed.png', url: '/accor-hera' },
    {
        name: 'Platinum Partner',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/platinum-partner-trimmed.png',
        url: '/preferred-hotels-and-resorts-platinum-partner-benefits',
    },
    {
        name: 'Select by Internova',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/select-by-internova-trimmed.png',
        url: '/select-by-internova',
    },
    {
        name: 'ABC\u2022CCRA Premier Hotel Program',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/abcccra-premier-hotel-program-trimmed.png',
        url: '/abc-ccra-premier-hotel-program',
    },
    {
        name: 'Travel Leaders Network',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/travel-leaders-network-trimmed.png',
        url: '/travel-leaders',
    },
    {
        name: 'Noble House VIP Select',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/noble-house-vip-select-trimmed.png',
        url: '/noble-house-vip-select',
    },
    {
        name: 'Trump Preferred Partner',
        logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/trump-preferred-partner-trimmed.png',
        url: '/trump-elite-partners',
    },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatMonthYear(dateString: string): string {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/* ------------------------------------------------------------------ */
/*  Luxury Circle Badge                                                */
/* ------------------------------------------------------------------ */

function LuxuryCircleBadge({ className, dark }: { className?: string; dark?: boolean }) {
    const color = dark ? '#0e2727' : '#F5F5F5';
    return (
        <span className={cn('inline-flex items-center gap-2', className)} aria-label="Luxury Circle Member">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" className="shrink-0" aria-hidden="true">
                <circle cx="11" cy="11" r="10.25" stroke={color} strokeWidth="0.5" opacity="0.5" />
                <circle cx="11" cy="11" r="7.5" stroke={color} strokeWidth="0.5" opacity="0.3" />
                <path d="M11 2.5L11.8 4L11 5.5L10.2 4Z" fill={color} opacity="0.6" />
                <text
                    x="11"
                    y="12.8"
                    textAnchor="middle"
                    fontFamily="'Didot', 'Georgia', serif"
                    fontSize="7"
                    fill={color}
                    opacity="0.85"
                    letterSpacing="1"
                >
                    LC
                </text>
            </svg>
            <span className={cn('lux-caps lux-caps-sm', dark ? 'text-[#0e2727]/70' : 'text-[#F5F5F5]/70')}>Luxury Circle</span>
        </span>
    );
}

/* ------------------------------------------------------------------ */
/*  Star Rating Display                                                */
/* ------------------------------------------------------------------ */

function StarRating({
    rating,
    interactive,
    onRate,
    dark,
    size = 'default',
}: {
    rating: number;
    interactive?: boolean;
    onRate?: (r: number) => void;
    dark?: boolean;
    size?: 'default' | 'sm';
}) {
    // Compact stars sit next to /50-opacity muted text — full-strength ivory
    // reads as shouty in that context, so the `sm` variant is dialed back to
    // /65 so it stays the focal element without overpowering the cluster.
    const filledDark = size === 'sm' ? 'text-[#F5F5F5]/65' : 'text-[#F5F5F5]';
    const filled = dark ? filledDark : 'text-[#0b2222]';
    const empty = dark ? 'text-[#F5F5F5]/20' : 'text-[#0b2222]/15';
    const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
    const gapClass = size === 'sm' ? 'gap-0.5' : 'gap-1';
    return (
        <div className={cn('flex', gapClass)}>
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={!interactive}
                    onClick={() => onRate?.(star)}
                    className={cn(interactive && 'cursor-pointer transition-colors duration-150', !interactive && 'cursor-default')}
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                    <svg className={cn(sizeClass, star <= rating ? filled : empty)} viewBox="0 0 20 20" fill="currentColor">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                </button>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Bridge: listens for `agent:open-inquiry` and opens the shared      */
/*  InquiryModal with agent-specific context. Must live inside         */
/*  PublicLayout so useInquiryModal() resolves to the real provider.   */
/* ------------------------------------------------------------------ */

function AgentInquiryBridge({ context }: { context: InquiryContext }) {
    const { openInquiry } = useInquiryModal();
    useEffect(() => {
        const handler = () => openInquiry(context);
        window.addEventListener('agent:open-inquiry', handler);
        return () => window.removeEventListener('agent:open-inquiry', handler);
    }, [openInquiry, context]);
    return null;
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

const ARTICLES_PREVIEW_COUNT = 4;
const INITIAL_REVIEWS = 4;

export default function AgentShow({ dataContext, agentReviews, isPreview }: AgentShowProps) {
    const shouldReduceMotion = useReducedMotion();

    // Public-path safety net: the backend guard 404s bogus slugs before they
    // reach us, but if a regression ever let null agent data through we do
    // NOT want to render the FAKE_AGENT sample profile in front of real users.
    // Render nothing instead. CMS preview legitimately needs the fake data.
    if (!dataContext?.agent && !isPreview) {
        return null;
    }

    /* ---- Data (fake fallback only for CMS preview) ---- */
    const isFakeAgent = !dataContext?.agent || dataContext.agent.slug === FAKE_AGENT.slug;
    const content = dataContext?.agent ?? FAKE_AGENT;
    const reviews = isFakeAgent ? FAKE_REVIEWS : (agentReviews ?? []);
    const rawGuides = (dataContext?.author_guides as AgentGuide[] | undefined) ?? [];
    const guides = isFakeAgent && rawGuides.length === 0 ? FAKE_ARTICLES : rawGuides;

    /* Derived profile — real data with display-layer defaults.
       Based In defaults to "United States", languages to ["English"], and
       specialties to the 4 default expertises (Expertise::default()) when
       the agent hasn't picked any. */
    const profile: AgentProfile = isFakeAgent
        ? FAKE_PROFILE
        : {
              based_in: content.based_in?.trim() || 'United States',
              languages: (content.languages && content.languages.length > 0 ? content.languages : ['en']).map(
                  (code) => LANGUAGE_LABELS[code] ?? code,
              ),
              specialties:
                  content.expertises && content.expertises.length > 0
                      ? content.expertises.map((e) => e.label)
                      : (dataContext?.default_expertises ?? []),
              socials: {
                  instagram: content.instagram_handle ? `https://instagram.com/${content.instagram_handle}` : undefined,
                  facebook: content.facebook_handle ? `https://facebook.com/${content.facebook_handle}` : undefined,
                  linkedin: content.linkedin_url || undefined,
                  twitter: content.twitter_handle ? `https://twitter.com/${content.twitter_handle}` : undefined,
                  tiktok: content.tiktok_handle ? `https://tiktok.com/@${content.tiktok_handle.replace(/^@/, '')}` : undefined,
                  pinterest: content.pinterest_handle ? `https://pinterest.com/${content.pinterest_handle}` : undefined,
                  youtube: content.youtube_url || undefined,
                  reddit: content.reddit_handle ? `https://reddit.com/${content.reddit_handle.replace(/^u_?/i, 'user/')}` : undefined,
                  website: content.website_url || undefined,
              },
          };

    /* ---- State ---- */
    const [reviewFormOpen, setReviewFormOpen] = useState(false);
    const [reviewSubmitted, setReviewSubmitted] = useState(false);
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewError, setReviewError] = useState<string | null>(null);
    const [reviewForm, setReviewForm] = useState<ReviewFormData>({
        name: '',
        email: '',
        rating: 5,
        review_text: '',
        website: '',
    });

    const [showAllReviews, setShowAllReviews] = useState(false);
    const [saveContactOpen, setSaveContactOpen] = useState(false);
    const [pageUrl, setPageUrl] = useState('');

    useEffect(() => {
        setPageUrl(window.location.href);
    }, []);

    /* ---- Derived ---- */
    const firstName = content.first_name;
    const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;
    const featuredReview = reviews.length > 0 ? reviews[0] : null;
    const remainingReviews = reviews.slice(1);
    const visibleReviews = showAllReviews ? remainingReviews : remainingReviews.slice(0, INITIAL_REVIEWS);
    const previewArticles = guides.slice(0, ARTICLES_PREVIEW_COUNT);
    const howItWorksSteps = buildHowItWorksSteps(firstName);
    const vCardUri = buildVCardDataUri(content);
    const vCardUrl = `https://luxrallytravel.com/the-team/${content.slug}?vcard=1`;

    const isMobile = useMemo(() => typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent), []);

    const sanitizedFirstParagraph = useMemo(() => {
        if (!content.bio) return null;
        const firstP = content.bio.match(/<p[^>]*>.*?<\/p>/i)?.[0] ?? content.bio;
        return DOMPurify.sanitize(firstP, { ALLOWED_TAGS: ['p', 'strong', 'em', 'a', 'br'], ALLOWED_ATTR: ['href', 'target', 'rel'] });
    }, [content.bio]);

    const handleSaveContact = useCallback(() => {
        if (isMobile) {
            // Direct download on mobile
            const a = document.createElement('a');
            a.href = vCardUri;
            a.download = `${content.first_name}-${content.last_name}.vcf`;
            a.click();
        } else {
            // Show QR modal on desktop
            setSaveContactOpen(true);
        }
    }, [isMobile, vCardUri, content.first_name, content.last_name]);

    /* ---- Trigger inquiry popup (replaces legacy drawer) ---- */
    const openAgentInquiry = useCallback(() => {
        window.dispatchEvent(new CustomEvent('agent:open-inquiry'));
    }, []);

    /* ---- Handlers ---- */
    const handleEmailClick = () => {
        if (content.email) window.location.href = `mailto:${content.email}`;
    };

    const handleReviewSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (reviewForm.website) return;
        setReviewSubmitting(true);
        setReviewError(null);
        try {
            await axios.post(`/api/agents/${content.id}/reviews`, {
                client_name: reviewForm.name,
                client_email: reviewForm.email,
                rating: reviewForm.rating,
                review_text: reviewForm.review_text,
            });
            setReviewSubmitted(true);
        } catch {
            setReviewError('Something went wrong. Please try again.');
        } finally {
            setReviewSubmitting(false);
        }
    };

    /* ---- Agent-scoped inquiry context (feeds the shared popup) ---- */
    const agentInquiryContext = useMemo<InquiryContext>(
        () => ({
            source: 'agent_page',
            agent_id: content.id,
            agent_name: firstName,
            agent_slug: content.slug,
            agent_photo: content.main_image ?? undefined,
            page_url: pageUrl,
        }),
        [content.id, content.slug, content.main_image, firstName, pageUrl],
    );

    /* ---- Motion helper — no-op when reduced motion preferred ---- */
    const mp = (props: Record<string, unknown>) => (shouldReduceMotion ? {} : props);

    /* Connect block — rendered inline with Based In / Languages at lg+ (icons
       collapse to a 160px-wide 2-row grid via lg:max-w-[160px] so it fits the
       column), and as its own row under the value-prop checkmarks at smaller
       widths so it can claim the full column width for a single-row icon strip. */
    const hasSocials = !!(profile.socials && Object.values(profile.socials).some(Boolean));
    const connectBlockContent = hasSocials ? (
        <>
            <p className="lux-caps lux-caps-sm mb-1.5 text-[#F5F5F5]/55">Connect</p>
            <div className="-mt-1 -ml-2 flex w-fit flex-wrap items-center lg:max-w-[160px]">
                {profile.socials!.instagram && (
                    <a
                        href={profile.socials!.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.facebook && (
                    <a
                        href={profile.socials!.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.linkedin && (
                    <a
                        href={profile.socials!.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.twitter && (
                    <a
                        href={profile.socials!.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="X (Twitter)"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.tiktok && (
                    <a
                        href={profile.socials!.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="TikTok"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.6 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.78 1.52V6.79a4.85 4.85 0 0 1-1.65-.1Z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.pinterest && (
                    <a
                        href={profile.socials!.pinterest}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Pinterest"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.youtube && (
                    <a
                        href={profile.socials!.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.reddit && (
                    <a
                        href={profile.socials!.reddit}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Reddit"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25c-.498 0-.927-.29-1.131-.71-1.066.74-2.546 1.207-4.18 1.273l.86-3.586 2.486.575c.03.66.566 1.187 1.226 1.187 0 0 .002 0 0 0zm-7.518 5.764c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25-1.25-.56-1.25-1.25.56-1.25 1.25-1.25zm5.018 0c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25-1.25-.56-1.25-1.25.56-1.25 1.25-1.25zm-7.45 4.453a.5.5 0 0 1 .353.146c.83.83 2.413 1.057 3.587 1.057 1.175 0 2.758-.227 3.588-1.057a.5.5 0 0 1 .708.708c-1.318 1.317-3.55 1.402-4.296 1.402-.746 0-2.978-.085-4.296-1.402a.5.5 0 0 1 .354-.854z" />
                        </svg>
                    </a>
                )}
                {profile.socials!.website && (
                    <a
                        href={profile.socials!.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Website"
                        className="inline-flex h-7 w-7 items-center justify-center text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                    >
                        <svg className="h-[14px] w-[14px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                    </a>
                )}
            </div>
        </>
    ) : null;

    return (
        <>
            <PublicLayout
                headerConfig={{
                    position: 'fixed',
                    scrollBehavior: 'always',
                    showMenu: false,
                    showGetStarted: false,
                    showSearch: false,
                    showPhone: false,
                    logoAlignment: 'left',
                    showCta: true,
                    ctaText: `Book with ${firstName}`,
                    ctaTextMobile: 'Book',
                    ctaAction: 'custom',
                    ctaCustomEventName: 'agent:open-inquiry',
                    ctaStyle: 'filled',
                    // Non-elite agents get the singular tagline; elite agents override with the Elite Advisor SVG below.
                    tagline: 'Luxury Travel Advisor',
                    ...(content.is_luxury && {
                        // Compact icon-only LR mark on mobile so the full "ELITE ADVISOR" badge fits beside it.
                        compactLogoOnMobile: true,
                        taglineNode: (
                            <div className="text-[#F5F5F5]/55 sm:text-[#F5F5F5]/40 md:-mr-2 md:mt-px">
                                <svg viewBox="0 0 380 28" fill="currentColor" aria-label="Elite Advisor" className="h-[11px] w-auto sm:h-3 md:h-3">
                                    <text
                                        x="0"
                                        y="27"
                                        fontFamily="ui-sans-serif, system-ui, sans-serif"
                                        fontWeight="800"
                                        fontSize="38"
                                        letterSpacing="0.12em"
                                        fontStyle="italic"
                                    >
                                        ELITE ADVISOR
                                    </text>
                                </svg>
                            </div>
                        ),
                    }),
                }}
            >
                <AgentInquiryBridge context={agentInquiryContext} />
                <MetaHead
                    title={`${content.full_name} — Travel Advisor | Luxrally Travel`}
                    description={
                        content.role
                            ? `${content.full_name}, ${content.role} at Luxrally Travel. Plan your bespoke luxury journey.`
                            : `${content.full_name} — Travel Advisor at Luxrally Travel.`
                    }
                />

                {/* Scoped hero animation */}
                {/* ============================================= */}
                {/* HERO — Single panel: portrait + content        */}
                {/* Teal backdrop mirrors /clients registration    */}
                {/* ============================================= */}
                <section className="w-full bg-[#0b2222]">
                    <div className="mx-auto max-w-[1600px] px-6 pt-12 pb-8 md:px-12 md:pt-16 md:pb-10 lg:px-20 lg:pt-20 lg:pb-12 xl:px-24">
                        <div className="grid grid-cols-1 items-start gap-20 lg:grid-cols-[minmax(0,1fr)_1px_clamp(380px,32vw,520px)] lg:gap-0">
                            {/* Left column — agent info */}
                            <motion.div
                                {...mp({
                                    variants: stagger,
                                    initial: 'hidden',
                                    animate: 'visible',
                                })}
                                className="sm:px-10 md:px-16 lg:px-0 lg:pr-6 xl:pr-12 2xl:pr-16"
                            >
                                {/* Identity block — photo + name + save contact */}
                                {/* Hide the circle when the advisor hasn't provided a real photo
                                    yet — the monogram placeholder stands on its own in collection
                                    thumbnails, but on the profile page the generated initials inside
                                    a circle frame would just duplicate the name. */}
                                <div className="mx-auto w-full max-w-[34rem] sm:mx-0 sm:max-w-none">
                                <motion.div
                                    {...mp({ variants: fadeUp })}
                                    className="flex flex-col items-center gap-7 text-center sm:flex-row sm:items-stretch sm:gap-5 sm:text-left lg:gap-4 xl:gap-6 2xl:gap-7"
                                >
                                    {content.main_image && !content.main_image.includes('/agents/placeholder-') && (
                                        <div className="relative aspect-[3/4] w-32 shrink-0 overflow-hidden rounded-sm sm:aspect-auto sm:w-32 sm:self-stretch lg:w-20 xl:w-24 2xl:w-28">
                                            <img
                                                src={content.main_image}
                                                alt={content.full_name}
                                                className="absolute inset-0 h-full w-full object-cover object-[center_20%]"
                                                loading="eager"
                                            />
                                        </div>
                                    )}

                                    {/* Name + save contact */}
                                    <div className="min-w-0">
                                        <h1 className="lux-didot text-[clamp(2.25rem,4vw,3.75rem)] leading-[0.9] text-[#F5F5F5] sm:text-[clamp(2.75rem,6vw,3.5rem)] sm:leading-none lg:text-[clamp(2.25rem,4vw,3.75rem)]">
                                            <span className="inline sm:block lg:inline">{content.first_name}</span>
                                            <span className="inline sm:hidden lg:inline">{' '}</span>
                                            <span className="inline sm:block lg:inline">{content.last_name}</span>
                                        </h1>
                                        <div className="mt-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 sm:justify-start">
                                            <TaglineBadge
                                                text="VERIFIED TRAVEL ADVISOR"
                                                leadingIcon="verified"
                                                className="h-2 w-auto text-[#F5F5F5]/50"
                                            />
                                            {reviews.length > 0 && (
                                                <a
                                                    href="#reviews"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        const target = document.getElementById('reviews');
                                                        if (target) {
                                                            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            history.pushState(null, '', '#reviews');
                                                        }
                                                    }}
                                                    aria-label={`Read all ${reviews.length} review${reviews.length !== 1 ? 's' : ''}`}
                                                    className="lux-caps lux-caps-sm group flex items-center gap-1.5 text-[#F5F5F5]/50 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                                                >
                                                    <StarRating rating={Math.round(avgRating)} dark size="sm" />
                                                    <span>
                                                        {avgRating.toFixed(1)} &middot; {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                                                    </span>
                                                    <ChevronDown
                                                        className="h-3 w-3 transition-transform duration-300 group-hover:translate-y-0.5"
                                                        strokeWidth={1.5}
                                                    />
                                                </a>
                                            )}
                                        </div>
                                        {content.show_became_agent_on_publicly && content.became_agent_on && (
                                            <p className="lux-body mt-1.5 flex items-center justify-center gap-1.5 text-[11px] leading-relaxed text-[#F5F5F5]/50 sm:justify-start">
                                                <svg
                                                    viewBox="0 0 16 16"
                                                    width="12"
                                                    height="12"
                                                    fill="currentColor"
                                                    aria-hidden="true"
                                                    className="flex-shrink-0"
                                                >
                                                    <rect x="1.5" y="1" width="0.8" height="14" />
                                                    <rect x="2.3" y="1.5" width="3" height="2" />
                                                    <rect x="8.3" y="1.5" width="3" height="2" />
                                                    <rect x="5.3" y="3.5" width="3" height="2" />
                                                    <rect x="11.3" y="3.5" width="2.5" height="2" />
                                                    <rect x="2.3" y="5.5" width="3" height="2" />
                                                    <rect x="8.3" y="5.5" width="3" height="2" />
                                                    <rect x="5.3" y="7.5" width="3" height="2" />
                                                    <rect x="11.3" y="7.5" width="2.5" height="2" />
                                                </svg>
                                                Became Agent On:{' '}
                                                {new Date(content.became_agent_on + 'T00:00:00').toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                })}
                                            </p>
                                        )}
                                        <div className="mt-1 flex flex-col items-center gap-1 sm:flex-row sm:items-center sm:gap-3">
                                            <p className="lux-body text-[11px] leading-relaxed text-[#F5F5F5]/50">{firstName} is a text away.</p>
                                            {content.show_save_contact && (
                                                <button
                                                    type="button"
                                                    onClick={handleSaveContact}
                                                    className="inline-flex items-center text-[#F5F5F5]/50 transition-colors duration-300 hover:text-[#F5F5F5]/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F5F5]"
                                                >
                                                    <TaglineBadge text="SAVE CONTACT" leadingIcon="save-contact" className="h-2 w-auto" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Bio prose — first paragraph only */}
                                {sanitizedFirstParagraph && (
                                    <motion.div {...mp({ variants: fadeUp })} className="mt-8 sm:mt-10">
                                        <div
                                            className="lux-body lux-body-sm text-center leading-[1.8] text-[#F5F5F5]/75 [&_p]:mb-4 [&_strong]:text-[#F5F5F5]/90 sm:text-left"
                                            dangerouslySetInnerHTML={{ __html: sanitizedFirstParagraph }}
                                        />
                                    </motion.div>
                                )}
                                </div>

                                {/* Credentials + Connect bar */}
                                <motion.div {...mp({ variants: fadeUp })} className="mt-8 border-t border-[#F5F5F5]/10 pt-6">
                                    <div className="flex flex-wrap items-start gap-x-8 gap-y-5">
                                        {profile.based_in && (
                                            <div>
                                                <p className="lux-caps lux-caps-sm mb-1.5 text-[#F5F5F5]/55">Based in</p>
                                                <p className="lux-body lux-body-sm text-[#F5F5F5]/80">{profile.based_in}</p>
                                            </div>
                                        )}
                                        {profile.languages.length > 0 && (
                                            <div>
                                                <p className="lux-caps lux-caps-sm mb-1.5 text-[#F5F5F5]/55">Languages</p>
                                                <div className="lux-body lux-body-sm flex flex-wrap gap-x-3 gap-y-0.5 text-[#F5F5F5]/80">
                                                    {profile.languages.map((lang) => (
                                                        <span key={lang}>{lang}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {connectBlockContent && <div className="hidden lg:block">{connectBlockContent}</div>}
                                    </div>
                                </motion.div>

                                {/* Specialties — pill tags */}
                                {profile.specialties.length > 0 && (
                                    <motion.div {...mp({ variants: fadeUp })} className="mt-6">
                                        <p className="lux-caps lux-caps-sm mb-2.5 text-[#F5F5F5]/55">Expertise</p>
                                        <div className="flex flex-wrap gap-2.5">
                                            {profile.specialties.map((specialty) => (
                                                <span
                                                    key={specialty}
                                                    className="lux-caps lux-caps-sm rounded-full bg-obsidian px-3.5 py-1.5 text-[#F5F5F5]/55 shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
                                                >
                                                    {specialty}
                                                </span>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Value props */}
                                <motion.div
                                    {...mp({ variants: fadeUp })}
                                    className="mt-8 inline-grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2 sm:gap-y-3"
                                >
                                    {[
                                        { key: 'cell', node: `${firstName}’s personal cell` },
                                        {
                                            key: 'fastbook',
                                            node: (
                                                <>
                                                    Proprietary FastBook<span className="-ml-2 align-super text-[8px] font-medium tracking-normal text-[#F5F5F5]/45">PP</span>{' '}
                                                    technology
                                                </>
                                            ),
                                        },
                                        { key: 'preferences', node: 'Travel preferences applied' },
                                        { key: 'network', node: 'Global hospitality network' },
                                    ].map(({ key, node }) => (
                                        <p key={key} className="lux-body lux-body-sm flex items-start gap-2 text-[#F5F5F5]/65">
                                            <span className="mt-0.5 shrink-0 text-[#F5F5F5]/40">✓</span>
                                            {node}
                                        </p>
                                    ))}
                                </motion.div>

                                {/* Standalone Connect — only when the inline version is hidden (<lg).
                                    Sits below the value-prop checkmarks so the social row gets the
                                    full column width as a single icon strip. */}
                                {connectBlockContent && (
                                    <motion.div {...mp({ variants: fadeUp })} className="mt-14 lg:hidden">
                                        {connectBlockContent}
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Vertical divider — desktop only */}
                            <div className="hidden self-stretch bg-[#F5F5F5]/12 lg:block" aria-hidden="true" />

                            {/* Right column — shared InquiryForm (matches /clients) */}
                            <motion.div
                                {...mp({
                                    initial: { opacity: 0, y: 20 },
                                    animate: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.2, ease: EASE } },
                                })}
                                className="sm:px-10 md:px-16 lg:-mt-4 lg:px-0 lg:pl-10 xl:pl-14"
                            >
                                {/* Custom form header — matches agent-name scale when stacked, drops down at lg+ for balance with the narrow form column */}
                                <header className="mb-8 text-center">
                                    <h2 className="lux-didot text-[clamp(2.25rem,4vw,3.75rem)] leading-[0.95] text-[#F5F5F5] lg:text-[28px] xl:text-[32px]">
                                        Book with {firstName}
                                    </h2>
                                    <TaglineBadge
                                        text="NO FEES · PRIVATE RATES · BOOKED IN MINUTES"
                                        className="mx-auto mt-5 h-[7px] w-auto max-w-full text-[#F5F5F5]/50"
                                    />
                                </header>
                                <InquiryForm context={agentInquiryContext} hideHeader />
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* CINEMA BAND — video interlude                  */}
                {/* ============================================= */}
                <section className="relative w-full overflow-hidden" style={{ height: 'clamp(50vh, 70vh, 80vh)' }}>
                    {/* Video background with breathing animation */}
                    <video
                        className="absolute inset-0 h-full w-full object-cover"
                        style={{ animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                        loop
                        muted
                        playsInline
                        autoPlay
                        preload="metadata"
                        poster="https://cdnweb.luxrallytravel.com/2026/03/7fd6ac1a-cc22-411f-8e24-c20b4c712833.png"
                    >
                        <source
                            src="https://cdnweb.luxrallytravel.com/parity-migration/2025/08/de567095-10a8-4840-9a9b-42d6d5a16450.mp4"
                            type="video/mp4"
                        />
                    </video>

                    {/* Image credit — top-left, matches homepage CinemaBandBlock */}
                    <p
                        className="absolute top-6 left-6 z-10 text-[11px] font-semibold uppercase text-[#F5F5F5] md:top-10 md:left-10 md:text-[13px]"
                        style={{ letterSpacing: '0.35em' }}
                    >
                        Rosewood Schloss Fuschl, Austria
                    </p>

                    {/* Bottom-edge scrim */}
                    <div
                        className="absolute inset-x-0 bottom-0 z-[1] h-2/5"
                        style={{
                            background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)',
                        }}
                    />

                    {/* Editorial band */}
                    <motion.div
                        {...mp({
                            initial: { opacity: 0, y: 12 },
                            whileInView: { opacity: 1, y: 0 },
                            viewport: { once: true, amount: 0.3 },
                            transition: { duration: 0.6, ease: EASE },
                        })}
                        className="absolute inset-x-0 bottom-0 z-10"
                    >
                        <div className="flex items-center justify-between px-6 py-5 md:px-10 md:py-6">
                            {/* Left: editorial text */}
                            <div className="flex flex-col gap-1">
                                <span className="lux-caps lux-caps-sm text-[#F5F5F5]/50">Forbes Travel Guide Endorsed Agency</span>
                                <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">For the Apex Traveler</h2>
                                <p className="lux-body lux-body-sm mt-0.5 text-[#F5F5F5]/60">Intelligence applied. Travel executed. No fees.</p>
                            </div>

                            {/* Right: CTA buttons — desktop */}
                            <div className="ml-8 hidden shrink-0 items-center gap-3 md:flex">
                                {content.client_referral_url && (
                                    <a
                                        href={content.client_referral_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="agent-referral-cta"
                                        className={cn(CTA_BTN_PRIMARY, 'px-4 py-1.5')}
                                    >
                                        Create your account
                                    </a>
                                )}
                                <button type="button" onClick={openAgentInquiry} className={cn(CTA_BTN, 'px-4 py-1.5 backdrop-blur-sm')}>
                                    Talk to {firstName}
                                </button>
                            </div>
                        </div>

                        {/* Mobile: full-width CTAs below text */}
                        <div className="flex flex-col gap-2 px-6 pb-5 md:hidden">
                            {content.client_referral_url && (
                                <a
                                    href={content.client_referral_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="agent-referral-cta-mobile"
                                    className={cn(CTA_BTN_PRIMARY, 'px-4 py-1.5')}
                                >
                                    Create your account
                                </a>
                            )}
                            <button type="button" onClick={openAgentInquiry} className={cn(CTA_BTN, 'px-4 py-1.5 backdrop-blur-sm')}>
                                Talk to {firstName}
                            </button>
                        </div>
                    </motion.div>
                </section>

                {/* ============================================= */}
                {/* ============================================= */}
                {/* HOW IT WORKS (teal)                            */}
                {/* ============================================= */}
                <section className="lux-space-lg nav-px w-full bg-[#0e2727]">
                    <div className="mx-auto max-w-[1600px]">
                        {/* Section header */}
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true, margin: '0px 0px -50px 0px' },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                            className="mb-16 flex flex-col items-center text-center md:mb-20"
                        >
                            <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/60">HOW IT WORKS</p>
                            <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">From Vision to Departure</h2>
                        </motion.div>

                        {/* Steps grid */}
                        <motion.div
                            {...mp({
                                variants: stagger,
                                initial: 'hidden',
                                whileInView: 'visible',
                                viewport: { once: true, margin: '-60px' },
                            })}
                            className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0"
                        >
                            {howItWorksSteps.map((step, idx) => {
                                const isLast = idx === howItWorksSteps.length - 1;

                                return (
                                    <motion.div
                                        key={`step-${idx}`}
                                        {...mp({ variants: fadeUp })}
                                        className="relative flex flex-col items-center text-center"
                                    >
                                        {/* Didot step number on a horizontal rule */}
                                        <div className="relative flex w-full items-center justify-center">
                                            {!isLast && (
                                                <div
                                                    className="absolute top-1/2 left-1/2 hidden h-px w-full -translate-y-1/2 bg-[#F5F5F5]/15 lg:block"
                                                    aria-hidden
                                                />
                                            )}
                                            <span className="lux-didot lux-didot-lg relative z-10 inline-block bg-[#0e2727] px-5 leading-none text-[#F5F5F5]">
                                                {step.num}
                                            </span>
                                        </div>

                                        <h3 className="lux-didot lux-didot-md mt-5 px-2 text-[#F5F5F5]">{step.title}</h3>
                                        <p className="lux-body lux-body-sm mt-2 max-w-[260px] text-[#F5F5F5]/75">{step.body}</p>
                                    </motion.div>
                                );
                            })}
                        </motion.div>

                        {/* Agent-specific CTA — borderless caps link (matches homepage) */}
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true, margin: '0px 0px -40px 0px' },
                                transition: { duration: 0.6, ease: EASE },
                            })}
                            className="mt-16 flex flex-col items-center md:mt-20"
                        >
                            <button
                                type="button"
                                onClick={openAgentInquiry}
                                className="group flex items-center gap-2 text-[#F5F5F5]/50 transition-opacity duration-300 hover:text-[#F5F5F5]/80"
                            >
                                <span className="lux-caps lux-caps-sm">Book through {firstName}</span>
                                <svg
                                    className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                >
                                    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </motion.div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* WHY LUXRALLY (snow)                            */}
                {/* ============================================= */}
                <section className="lux-space-lg nav-px w-full bg-[#fdfaf3]">
                    <div className="mx-auto max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl">
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                            className="mx-auto mb-16 max-w-3xl text-center md:mb-20"
                        >
                            <p className="lux-caps lux-caps-sm mb-3 text-[#0e2727]/60">WHY LUXRALLY?</p>
                            <h2 className="lux-didot lux-didot-lg text-[#0e2727]">We Know What You Want</h2>
                        </motion.div>

                        <motion.div
                            {...mp({
                                variants: stagger,
                                initial: 'hidden',
                                whileInView: 'visible',
                                viewport: { once: true, margin: '-60px' },
                            })}
                            className="grid grid-cols-1 gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-16 lg:gap-y-16"
                        >
                            {VALUE_PROPS.map((item, i) => (
                                <motion.div key={i} {...mp({ variants: fadeUp })} className="relative flex flex-col items-center text-center">
                                    <p className="lux-caps lux-caps-sm mb-2 text-[#0e2727]/60">{item.kicker}</p>
                                    <h3 className="lux-didot lux-didot-md text-[#0e2727]">{item.headline}</h3>
                                    <p className="lux-body lux-body-sm mt-3 max-w-[34ch] leading-relaxed text-[#0e2727]/70">{item.body}</p>
                                </motion.div>
                            ))}
                        </motion.div>

                        {/* Agent-specific CTA — borderless caps link (matches homepage) */}
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true, margin: '0px 0px -40px 0px' },
                                transition: { duration: 0.6, ease: EASE },
                            })}
                            className="mt-20 flex flex-col items-center text-center md:mt-24"
                        >
                            <button
                                type="button"
                                onClick={openAgentInquiry}
                                className="group flex items-center gap-2 text-[#0e2727]/40 transition-opacity duration-300 hover:text-[#0e2727]/60"
                            >
                                <span className="lux-caps lux-caps-sm">
                                    {content.phone ? `Text ${content.full_name}` : `Connect with ${content.full_name}`}
                                </span>
                                <svg
                                    className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                >
                                    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </motion.div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* ============================================= */}
                {/* REVIEWS (teal)                                 */}
                {/* ============================================= */}
                <section id="reviews" className="lux-space-lg nav-px w-full bg-[#0b2222]">
                        <div className="mx-auto max-w-[1600px]">
                            <motion.div
                                {...mp({
                                    variants: stagger,
                                    initial: 'hidden',
                                    whileInView: 'visible',
                                    viewport: { once: true, amount: 0.1 },
                                })}
                            >
                                {/* Header */}
                                <motion.div
                                    {...mp({ variants: fadeUp })}
                                    className={cn('text-center', reviews.length > 0 ? 'mb-16 md:mb-20' : 'mb-10 md:mb-12')}
                                >
                                    <p className="lux-caps lux-caps-sm text-[#F5F5F5]/60">Client Experiences</p>
                                    <h2 className="lux-didot lux-didot-lg mt-3 text-[#F5F5F5]">Testimonials</h2>
                                    {reviews.length > 0 ? (
                                        <div className="mt-3 flex items-center justify-center gap-3">
                                            <StarRating rating={Math.round(avgRating)} dark />
                                            <span className="lux-body lux-body-sm text-[#F5F5F5]/60">
                                                {avgRating.toFixed(1)} &middot; {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    ) : (
                                        <p className="lux-body lux-body-sm mx-auto mt-5 max-w-[48ch] leading-relaxed text-[#F5F5F5]/70">
                                            Be the first to share your experience with {firstName}. Your review helps future travelers find the right advisor.
                                        </p>
                                    )}
                                </motion.div>

                                {/* Featured pull-quote */}
                                {featuredReview && (
                                    <motion.blockquote {...mp({ variants: fadeUp })} className="mb-16 md:mb-20">
                                        <p className="lux-didot lux-didot-lg leading-snug text-[#F5F5F5]/80">
                                            &ldquo;{featuredReview.review_text}&rdquo;
                                        </p>
                                        <footer className="mt-6 flex items-center gap-3">
                                            <StarRating rating={featuredReview.rating} dark />
                                            <span className="lux-body lux-body-sm text-[#F5F5F5]/60">{featuredReview.client_name}</span>
                                        </footer>
                                    </motion.blockquote>
                                )}

                                {/* Remaining reviews — 2-col grid on desktop */}
                                {remainingReviews.length > 0 && (
                                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
                                        <AnimatePresence initial={false}>
                                            {visibleReviews.map((review) => (
                                                <motion.div
                                                    key={review.id}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } }}
                                                    exit={{ opacity: 0, y: 20, transition: { duration: 0.3, ease: EASE } }}
                                                    className="border-t border-[#F5F5F5]/10 pt-8"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <StarRating rating={review.rating} dark />
                                                        <span className="lux-body lux-body-sm text-[#F5F5F5]/60">
                                                            {formatMonthYear(review.created_at)}
                                                        </span>
                                                    </div>
                                                    <p className="lux-body lux-body-lg mt-4 leading-relaxed text-[#F5F5F5]/75">
                                                        &ldquo;{review.review_text}&rdquo;
                                                    </p>
                                                    <p className="lux-body lux-body-sm mt-4 text-[#F5F5F5]/60">{review.client_name}</p>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </div>
                                )}

                                {/* Show all / show less */}
                                {remainingReviews.length > INITIAL_REVIEWS && (
                                    <motion.div {...mp({ variants: fadeUp })} className="mt-10">
                                        <button
                                            type="button"
                                            onClick={() => setShowAllReviews(!showAllReviews)}
                                            className="lux-caps lux-caps-sm text-[#F5F5F5]/60 transition-colors duration-300 hover:text-[#F5F5F5]"
                                        >
                                            {showAllReviews ? 'Show Less' : `Show All ${reviews.length} Reviews`}
                                        </button>
                                    </motion.div>
                                )}

                                {/* Review form toggle — CTA is the primary action when there are no reviews yet */}
                                <motion.div
                                    {...mp({ variants: fadeUp })}
                                    className={cn('text-center', reviews.length > 0 ? 'mt-16' : 'mt-4')}
                                >
                                    {!reviewFormOpen && !reviewSubmitted && (
                                        <button type="button" onClick={() => setReviewFormOpen(true)} className={cn(CTA_BTN, 'px-6 py-2.5')}>
                                            {reviews.length === 0 ? 'Leave the First Review' : 'Share Your Experience'} &rarr;
                                        </button>
                                    )}

                                    <AnimatePresence mode="wait">
                                        {reviewFormOpen && !reviewSubmitted && (
                                            <motion.form
                                                key="review-form"
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                transition={{ duration: 0.4, ease: EASE }}
                                                onSubmit={handleReviewSubmit}
                                                className="mx-auto max-w-xl space-y-8 overflow-hidden text-left"
                                            >
                                                {/* Editorial intro — value-tag heading + hairline */}
                                                <div className="text-center">
                                                    <TaglineBadge text="SHARE YOUR JOURNEY" className="mx-auto h-[14px] w-auto text-[#F5F5F5]" />
                                                    <div className="mx-auto mt-5 h-px w-10 bg-[#F5F5F5]/25" aria-hidden="true" />
                                                    <p className="lux-body lux-body-sm mx-auto mt-5 max-w-[64ch] leading-relaxed text-[#F5F5F5]/70">
                                                        The moments that stayed with you, the details worth remembering — your words help future travelers trust the process.
                                                    </p>
                                                </div>

                                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                                    <div>
                                                        <label className="mb-3 flex items-center gap-2.5">
                                                            <TaglineBadge text="YOUR NAME" className="h-[10px] w-auto text-[#F5F5F5]/70" />
                                                            <span className="text-[10px] font-normal tracking-wide text-[#F5F5F5]/40 lowercase">(displayed)</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            placeholder="First & Last"
                                                            required
                                                            value={reviewForm.name}
                                                            onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))}
                                                            className={INPUT_CLS}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="mb-3 flex items-center gap-2.5">
                                                            <TaglineBadge text="EMAIL" className="h-[10px] w-auto text-[#F5F5F5]/70" />
                                                            <span className="text-[10px] font-normal tracking-wide text-[#F5F5F5]/40 lowercase">(kept private)</span>
                                                        </label>
                                                        <input
                                                            type="email"
                                                            placeholder="you@example.com"
                                                            required
                                                            value={reviewForm.email}
                                                            onChange={(e) => setReviewForm((f) => ({ ...f, email: e.target.value }))}
                                                            className={INPUT_CLS}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="mb-3 flex items-center gap-2.5">
                                                        <TaglineBadge text="RATING" className="h-[10px] w-auto text-[#F5F5F5]/70" />
                                                        <span className="text-[10px] font-normal tracking-wide text-[#F5F5F5]/40 lowercase">(displayed)</span>
                                                    </label>
                                                    <div className="[&_svg]:h-6 [&_svg]:w-6">
                                                        <StarRating
                                                            rating={reviewForm.rating}
                                                            interactive
                                                            dark
                                                            onRate={(r) => setReviewForm((f) => ({ ...f, rating: r }))}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="mb-3 flex items-center gap-2.5">
                                                        <TaglineBadge text="YOUR EXPERIENCE" className="h-[10px] w-auto text-[#F5F5F5]/70" />
                                                        <span className="text-[10px] font-normal tracking-wide text-[#F5F5F5]/40 lowercase">(displayed)</span>
                                                    </label>
                                                    <textarea
                                                        placeholder="Describe the journey — the moments, the service, the details worth remembering…"
                                                        required
                                                        rows={6}
                                                        value={reviewForm.review_text}
                                                        onChange={(e) => setReviewForm((f) => ({ ...f, review_text: e.target.value }))}
                                                        className={cn(INPUT_CLS, 'resize-none leading-relaxed')}
                                                    />
                                                </div>

                                                {/* Honeypot */}
                                                <div className="absolute -left-[9999px]" aria-hidden="true">
                                                    <input
                                                        type="text"
                                                        name="website"
                                                        tabIndex={-1}
                                                        autoComplete="off"
                                                        value={reviewForm.website}
                                                        onChange={(e) => setReviewForm((f) => ({ ...f, website: e.target.value }))}
                                                    />
                                                </div>

                                                {reviewError && (
                                                    <p className="lux-body lux-body-sm text-center text-[#F87171]">{reviewError}</p>
                                                )}

                                                <div className="flex flex-col items-center gap-5 pt-2">
                                                    <button
                                                        type="submit"
                                                        disabled={reviewSubmitting}
                                                        className={cn(CTA_BTN, 'min-w-[220px] px-8 py-3 disabled:opacity-50')}
                                                    >
                                                        {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setReviewFormOpen(false)}
                                                        className="lux-caps lux-caps-sm text-[#F5F5F5]/55 transition-colors duration-300 hover:text-[#F5F5F5]/80"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </motion.form>
                                        )}

                                        {reviewSubmitted && (
                                            <motion.div
                                                key="review-success"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.5, ease: EASE }}
                                                className="flex flex-col items-center py-12 text-center"
                                                aria-live="polite"
                                                role="status"
                                            >
                                                <CheckCircle className="mb-6 h-12 w-12 text-[#F5F5F5]/80" strokeWidth={1} />
                                                <h3 className="lux-didot lux-didot-md mb-3 text-[#F5F5F5]">Thank You</h3>
                                                <p className="lux-body lux-body-sm text-[#F5F5F5]/60">
                                                    Your review has been submitted and will appear after approval.
                                                </p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            </motion.div>
                        </div>
                    </section>

                {/* ============================================= */}
                {/* Exclusive Access — partner benefits (teal)     */}
                {/* ============================================= */}
                <section className="nav-px w-full bg-[#0e2727] py-16 md:py-20">
                    <div className="mx-auto max-w-[1600px]">
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                            className="mb-10 text-center md:mb-14"
                        >
                            <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/60">Exclusive Access</p>
                            <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">Unlocked with {firstName}</h2>
                        </motion.div>

                        <motion.div
                            {...mp({
                                variants: stagger,
                                initial: 'hidden',
                                whileInView: 'visible',
                                viewport: { once: true, margin: '-40px' },
                            })}
                            className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                        >
                            {BENEFIT_LOGOS.map((logo) => {
                                const sources = logoPictureSources(logo.logo);
                                const { width, height } = partnerLogoIntrinsicSize(logo.logo);
                                const imgClass = cn(
                                    'w-auto object-contain opacity-50 brightness-0 invert transition-opacity duration-200 hover:opacity-80',
                                    logo.name.includes('Four Seasons') ? 'h-16 -translate-y-[33px]' : 'h-8',
                                );
                                return (
                                    <motion.div key={logo.name} {...mp({ variants: fadeUp })}>
                                        <Link
                                            href={logo.url}
                                            className="flex items-center justify-center rounded-sm px-4 py-5 transition-opacity duration-200 hover:opacity-100"
                                        >
                                            {sources ? (
                                                <picture>
                                                    <source type="image/avif" srcSet={sources.avif} />
                                                    <source type="image/webp" srcSet={sources.webp} />
                                                    <img src={logo.logo} alt={logo.name} className={imgClass} draggable={false} width={width} height={height} />
                                                </picture>
                                            ) : (
                                                <img src={logo.logo} alt={logo.name} className={imgClass} draggable={false} width={width} height={height} />
                                            )}
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* CLOSING CTA (teal)                            */}
                {/* ============================================= */}
                <motion.section
                    className="lux-space-lg nav-px w-full bg-[#0b2222]"
                    {...mp({
                        initial: 'hidden',
                        whileInView: 'visible',
                        viewport: { once: true, amount: 0.3 },
                        variants: fadeUp,
                    })}
                >
                    <div className="mx-auto max-w-[1600px]">
                        <div className="mx-auto max-w-2xl text-center">
                            <h2 className="lux-didot lux-didot-xl text-[#F5F5F5]">Ready to start planning?</h2>
                            <p className="lux-body lux-body-lg mt-5 text-[#F5F5F5]/60">No fees. Better rates. VIP treatment at every hotel.</p>
                            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                                {content.client_referral_url && (
                                    <a
                                        href={content.client_referral_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="agent-referral-cta-footer"
                                        className={cn(CTA_BTN_PRIMARY, 'px-4 py-1.5')}
                                    >
                                        Create your account
                                    </a>
                                )}
                                <button type="button" onClick={openAgentInquiry} className={cn(CTA_BTN, 'inline-block px-4 py-1.5')}>
                                    New Client Form
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.section>

                {/* ============================================= */}
                {/* ============================================= */}
                {/* ARTICLES / JOURNAL (ivory)                     */}
                {/* Matches /journal SectionCompact format          */}
                {/* ============================================= */}
                {guides.length > 0 && (
                    <section className="lux-space-px scroll-mt-28 bg-[#fdfaf3] md:scroll-mt-32">
                        <div className="mx-auto max-w-[1400px] py-14 lg:py-20">
                            <motion.div
                                {...mp({
                                    variants: stagger,
                                    initial: 'hidden',
                                    whileInView: 'visible',
                                    viewport: { once: true, amount: 0.1 },
                                })}
                            >
                                {/* Compact header */}
                                <motion.div {...mp({ variants: fadeUp })} className="mb-8 lg:mb-10">
                                    <span className="lux-caps lux-caps-sm mb-1.5 block text-[#0e2727]/70">The Journal</span>
                                    <div className="flex items-baseline justify-between gap-6">
                                        <h2 className="lux-didot lux-didot-xl text-[#0e2727]">Written by {firstName}</h2>
                                        {guides.length > ARTICLES_PREVIEW_COUNT && content.author_slug && (
                                            <a
                                                href={`/journal/author/${content.author_slug}`}
                                                className="lux-caps lux-caps-sm inline-flex shrink-0 items-center gap-1.5 text-[#0e2727]/70 transition-colors hover:text-[#0e2727]"
                                            >
                                                See all
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </a>
                                        )}
                                    </div>
                                    <div className="mt-4 flex items-center gap-0">
                                        <div className="h-0.5 w-10 bg-[#0e2727]/70" />
                                        <div className="h-px flex-1 bg-[#0e2727]/70 opacity-50" />
                                    </div>
                                </motion.div>

                                {/* Single row of 4 vertical cards */}
                                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                                    {previewArticles.map((guide) => (
                                        <motion.div key={guide.id} {...mp({ variants: fadeUp })}>
                                            <a href={'/journal/' + guide.slug} className="group block">
                                                {guide.main_image ? (
                                                    <div className="relative aspect-[3/2] overflow-hidden">
                                                        <img
                                                            src={guide.main_image}
                                                            alt={guide.title}
                                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="relative flex aspect-[3/2] items-center justify-center bg-[#0e2727]/10">
                                                        <span className="lux-caps lux-caps-sm text-[#0e2727]/20">No Image</span>
                                                    </div>
                                                )}
                                                <div className="pt-4">
                                                    {guide.tags && <span className="lux-caps lux-caps-sm text-[#0e2727]/70">{guide.tags}</span>}
                                                    <h4 className="lux-didot lux-didot-md mt-1 text-[#0e2727] transition-colors group-hover:text-[#0e2727]/70">
                                                        {guide.title}
                                                    </h4>
                                                    {guide.excerpt && (
                                                        <p className="lux-body lux-body-sm mt-2 line-clamp-2 text-[#0e2727]/70">{guide.excerpt}</p>
                                                    )}
                                                </div>
                                            </a>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </section>
                )}

                {/* ============================================= */}
                {/* Bottom padding spacer for mobile sticky CTA */}
                <div className="h-20 lg:hidden" />
            </PublicLayout>

            {/* ============================================= */}
            {/* Portal: Mobile Sticky CTA + Inquiry Drawer    */}
            {/* Rendered outside PublicLayout to avoid the     */}
            {/* CSS transform containing-block issue.          */}
            {/* ============================================= */}
            {typeof document !== 'undefined' &&
                createPortal(
                    <>
                        {/* Mobile Sticky Bottom CTA */}
                        <div
                            className="fixed inset-x-0 bottom-0 z-40 lg:hidden"
                            style={{ backgroundColor: '#0e2727', paddingBottom: 'env(safe-area-inset-bottom)' }}
                        >
                            <div className="px-4 py-3">
                                <button type="button" onClick={openAgentInquiry} className={cn(CTA_BTN, 'w-full py-3 text-center')}>
                                    Ready to start planning?
                                </button>
                            </div>
                        </div>

                        {/* Save Contact — QR Code Modal (desktop) */}
                        <AnimatePresence>
                            {saveContactOpen && (
                                <>
                                    <motion.div
                                        key="qr-backdrop"
                                        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        onClick={() => setSaveContactOpen(false)}
                                    />
                                    <motion.div
                                        key="qr-modal"
                                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.3, ease: EASE }}
                                    >
                                        <div
                                            className="relative w-full max-w-sm rounded-sm bg-[#0e2727] p-10 text-center shadow-2xl"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setSaveContactOpen(false)}
                                                className="absolute top-4 right-4 text-[#F5F5F5]/40 transition-colors hover:text-[#F5F5F5]"
                                            >
                                                <X className="h-5 w-5" strokeWidth={1.5} />
                                            </button>

                                            <h3 className="lux-didot lux-didot-md text-[#F5F5F5]">Save {firstName}&rsquo;s Contact</h3>
                                            <p className="lux-body lux-body-sm mt-2 text-[#F5F5F5]/60">Scan with your phone to add to contacts</p>

                                            <div className="mx-auto mt-8 mb-8 inline-block rounded-sm bg-white p-4">
                                                {/* Encode raw vCard text — phone QR scanners (iOS Camera, Google Lens)
                                                    do not follow data: URIs but do recognize BEGIN:VCARD payloads. */}
                                                <QRCodeSVG value={buildVCard(content)} size={200} level="M" bgColor="#ffffff" fgColor="#0e2727" />
                                            </div>

                                            <p className="lux-body lux-body-sm text-[#F5F5F5]/60">
                                                or{' '}
                                                <a
                                                    href={vCardUri}
                                                    download={`${content.first_name}-${content.last_name}.vcf`}
                                                    className="!text-[#F5F5F5] underline transition-colors hover:!text-[#F5F5F5]"
                                                >
                                                    download the contact file
                                                </a>
                                            </p>
                                        </div>
                                    </motion.div>
                                </>
                            )}
                        </AnimatePresence>
                    </>,
                    document.body,
                )}
        </>
    );
}
